export type ThinkingWrapperKind = 'think' | 'thinking' | 'analysis';

export interface ThinkingParseOptions {
  /**
   * If provided, enables model-specific parsing quirks.
   * For now we only special-case gpt-oss style models.
   */
  model?: string | null;
}

export interface ThinkingParseResult {
  /**
   * Aggregated extracted thinking text blocks, in the order encountered.
   */
  thinking: string[];
  /**
   * The remaining text after stripping completed thinking blocks.
   */
  content: string;
  /**
   * True if we are currently inside an unterminated thinking wrapper (stream still ongoing).
   */
  inThinking: boolean;
}

type WrapperDef = {
  kind: ThinkingWrapperKind;
  open: string;
  close: string;
};

const COMMON_WRAPPERS: WrapperDef[] = [
  { kind: 'think', open: '<think>', close: '</think>' },
  { kind: 'thinking', open: '<thinking>', close: '</thinking>' },
  { kind: 'analysis', open: '<analysis>', close: '</analysis>' },
];

// Some models emit uppercase variants.
const CASE_INSENSITIVE_WRAPPERS: WrapperDef[] = [
  { kind: 'think', open: '<THINK>', close: '</THINK>' },
  { kind: 'thinking', open: '<THINKING>', close: '</THINKING>' },
  { kind: 'analysis', open: '<ANALYSIS>', close: '</ANALYSIS>' },
];

function isGptOssModel(model?: string | null) {
  const m = (model || '').toLowerCase();
  // Temporary heuristic: your new model id contains "gpt-oss".
  return m.includes('gpt-oss') || m.includes('gptoss');
}

function looksLikeHarmonyMarkup(text: string) {
  // Observed gpt-oss style: <|start|>assistant<|channel|>analysis<|message|>...<|end|>
  return (
    text.includes('<|start|>') &&
    text.includes('<|channel|>') &&
    text.includes('<|message|>')
  );
}

type HarmonyParseResult = {
  thinking: string[];
  content: string;
  inThinking: boolean;
};

function parseHarmonyMarkup(text: string): HarmonyParseResult {
  const START = '<|start|>';
  const END = '<|end|>';
  const CHANNEL = '<|channel|>';
  const MESSAGE = '<|message|>';

  let i = 0;
  const thinking: string[] = [];
  let content = '';
  let inThinking = false;

  while (true) {
    const startIdx = text.indexOf(START, i);
    if (startIdx === -1) {
      // Preserve any trailing plain text (rare, but keep it).
      content += text.slice(i);
      break;
    }

    // Preserve any plain text between segments.
    if (startIdx > i) {
      content += text.slice(i, startIdx);
    }

    const channelIdx = text.indexOf(CHANNEL, startIdx + START.length);
    const msgIdx = text.indexOf(MESSAGE, startIdx + START.length);

    // If malformed, bail out and keep the rest as content.
    if (channelIdx === -1 || msgIdx === -1 || msgIdx < channelIdx) {
      content += text.slice(startIdx);
      break;
    }

    const channelValueStart = channelIdx + CHANNEL.length;
    const channelValue = text.slice(channelValueStart, msgIdx);
    const messageStart = msgIdx + MESSAGE.length;
    const endIdx = text.indexOf(END, messageStart);

    if (endIdx === -1) {
      // Streaming: segment hasn't ended yet.
      const partial = text.slice(messageStart);
      if (channelValue === 'analysis') {
        if (partial.trim()) thinking.push(partial);
        inThinking = true;
      } else if (channelValue === 'final') {
        content += partial;
      } else {
        // Unknown channel: treat as content.
        content += partial;
      }
      break;
    }

    const messageText = text.slice(messageStart, endIdx);
    if (channelValue === 'analysis') {
      thinking.push(messageText);
    } else if (channelValue === 'final') {
      content += messageText;
    } else {
      // Unknown channel: treat as content.
      content += messageText;
    }

    i = endIdx + END.length;
  }

  return { thinking, content, inThinking };
}

function parseTagWrappersOnly(
  text: string,
  wrappers: WrapperDef[]
): ThinkingParseResult {
  if (!text) return { thinking: [], content: '', inThinking: false };

  let remaining = text;
  const extracted: string[] = [];
  let inThinking = false;

  // We'll repeatedly find the earliest opening tag among known wrappers.
  while (true) {
    let earliest:
      | { idx: number; def: WrapperDef }
      | { idx: -1; def: WrapperDef | null } = { idx: -1, def: null };

    for (const def of wrappers) {
      const idx = remaining.indexOf(def.open);
      if (idx !== -1 && (earliest.idx === -1 || idx < earliest.idx)) {
        earliest = { idx, def };
      }
    }

    if (earliest.idx === -1 || !earliest.def) break;

    const def = earliest.def;
    const openIdx = earliest.idx;
    const afterOpenIdx = openIdx + def.open.length;
    const closeIdx = remaining.indexOf(def.close, afterOpenIdx);

    if (closeIdx === -1) {
      // Streaming: open tag found but no close yet.
      inThinking = true;
      const thinkChunk = remaining.slice(afterOpenIdx);
      if (thinkChunk.trim().length > 0) extracted.push(thinkChunk);
      // Remove the opened tag and everything after it from content display.
      remaining = remaining.slice(0, openIdx);
      break;
    }

    const thinkText = remaining.slice(afterOpenIdx, closeIdx);
    extracted.push(thinkText);

    // Remove this whole think block from remaining content.
    remaining =
      remaining.slice(0, openIdx) + remaining.slice(closeIdx + def.close.length);
  }

  // If we're *not* inThinking, but there are stray close tags, strip them.
  if (!inThinking) {
    for (const def of wrappers) {
      if (remaining.includes(def.close)) {
        remaining = remaining.split(def.close).join('');
      }
    }
  }

  return { thinking: extracted, content: remaining, inThinking };
}

/**
 * Extracts one or more thinking blocks from a text payload.
 *
 * Supports multiple wrappers (e.g. multiple <think>...</think> occurrences).
 * Streaming-safe: if a wrapper opens but doesn't close yet, we consider ourselves "inThinking"
 * and remove the opened tag + thinking text from `content` so it isn't shown to the user.
 */
export function parseThinkingFromText(
  text: string,
  opts: ThinkingParseOptions = {}
): ThinkingParseResult {
  if (!text) return { thinking: [], content: '', inThinking: false };

  // "Harmony" style (observed in gpt-oss): <|start|>assistant<|channel|>analysis/final<|message|>...<|end|>
  // We map channel=analysis -> thinking blocks, channel=final -> user-visible content.
  if (looksLikeHarmonyMarkup(text) || isGptOssModel(opts.model)) {
    const harmony = parseHarmonyMarkup(text);
    const wrappers = [...COMMON_WRAPPERS, ...CASE_INSENSITIVE_WRAPPERS];
    const tagParsed = parseTagWrappersOnly(harmony.content, wrappers);
    return {
      thinking: [...harmony.thinking, ...tagParsed.thinking],
      content: tagParsed.content,
      inThinking: harmony.inThinking || tagParsed.inThinking,
    };
  }

  const wrappers = isGptOssModel(opts.model)
    ? [...COMMON_WRAPPERS, ...CASE_INSENSITIVE_WRAPPERS]
    : [...COMMON_WRAPPERS, ...CASE_INSENSITIVE_WRAPPERS];

  return parseTagWrappersOnly(text, wrappers);
}


